import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

// CTI Logo - local asset
const CTI_LOGO = "/cti-logo.png";

export default function LoginPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await login(formData.email, formData.password);
      toast.success('Willkommen zurück!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Ein Fehler ist aufgetreten';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/20 via-transparent to-lime-500/10" />
        <div className="relative z-10 flex flex-col justify-center px-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-4 mb-8">
              <img 
                src={CTI_LOGO} 
                alt="CTI Verwaltungs GmbH" 
                className="w-16 h-16 object-contain"
              />
              <h1 className="text-3xl font-heading font-bold text-foreground">CTIme App</h1>
            </div>
            <h2 className="text-4xl font-heading font-bold text-foreground mb-4 leading-tight">
              Zeitererfassung bei<br />CTI
            </h2>
            <p className="text-lg text-muted-foreground max-w-md">
              Arbeitszeiten erfassen und bearbeiten
            </p>
          </motion.div>

          {/* Feature highlights */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-12 space-y-4"
          >
            {[
              'Stunden Projekten und Tätigkeiten zuordnen',
              'Urlaubs- und Abwesenheitszeiten erfassen',
              'Für eine einfache und transparente Zeiterfassung im Arbeitsalltag',
            ].map((feature, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-lime-400" />
                <span className="text-muted-foreground">{feature}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-md"
        >
          {/* Mobile logo */}
          <div className="lg:hidden flex flex-col items-center justify-center gap-3 mb-8">
            <img 
              src={CTI_LOGO} 
              alt="CTI Verwaltungs GmbH" 
              className="w-14 h-14 object-contain"
            />
            <h1 className="text-2xl font-heading font-bold text-foreground">CTIme App</h1>
          </div>

          <div className="card-glass p-8">
            <div className="mb-8">
              <h2 className="text-2xl font-heading font-bold text-foreground">
                Anmelden
              </h2>
              <p className="text-muted-foreground mt-2">
                Melden Sie sich mit Ihren Zugangsdaten an
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-muted-foreground">
                  E-Mail
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  data-testid="email-input"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-background border-border focus:border-ring text-foreground"
                  placeholder="name@co-cti.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-muted-foreground">
                  Passwort
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    data-testid="password-input"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="bg-background border-border focus:border-ring text-foreground pr-10"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-muted-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                data-testid="submit-button"
                disabled={isLoading}
                className="w-full bg-indigo-500 hover:bg-indigo-600 text-white h-11 glow-primary"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Anmelden'
                )}
              </Button>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
